'use client';

import { useCallback, useEffect, useState } from 'react';
import {
  CONSENT_EVENT,
  CONSENT_REOPEN_EVENT,
  type ConsentChoice,
  type PurposeAnswers,
  readConsent,
  writeConsent,
} from '@/lib/consent';

/**
 * The visitor's answer, and the two things that change it.
 *
 * `undefined` while the answer has not been read yet — which is every server
 * render, and the first client render that has to match it. Distinguishing that
 * from `null` (asked, no answer) is what keeps the banner from flashing onto a
 * page belonging to someone who settled the question months ago, and what keeps
 * the analytics tag from loading for a moment before the refusal is read.
 *
 * A window event rather than a context provider: the banner and the tag are
 * mounted in different parts of the tree and only ever need to hear "the answer
 * changed", so a provider wrapping the whole application would be machinery
 * around a single boolean.
 */
export function useConsent() {
  const [choice, setChoice] = useState<ConsentChoice | null | undefined>(undefined);

  useEffect(() => {
    setChoice(readConsent(window.localStorage));

    const onChanged = () => setChoice(readConsent(window.localStorage));
    window.addEventListener(CONSENT_EVENT, onChanged);
    /* Another tab answering counts as answering. Without this, a visitor who
       accepts in one tab keeps being asked in the others. */
    window.addEventListener('storage', onChanged);
    return () => {
      window.removeEventListener(CONSENT_EVENT, onChanged);
      window.removeEventListener('storage', onChanged);
    };
  }, []);

  const decide = useCallback((answers: PurposeAnswers) => {
    writeConsent(window.localStorage, answers);
    window.dispatchEvent(new Event(CONSENT_EVENT));
  }, []);

  return { choice, decide };
}

/** Asks the banner to reappear so an answer can be changed. */
export function reopenConsent() {
  window.dispatchEvent(new Event(CONSENT_REOPEN_EVENT));
}
